import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Plus, X, Archive, ArchiveRestore, Trash2, ChevronDown, ChevronUp, MessageSquare, BookMarked, Brain,
  Loader2, Sparkles, CheckCircle2, AlertCircle,
} from 'lucide-react';
import InlineChat, { Message, newMessageId } from './InlineChat';
import { PortalBadge } from './arkorbit-ui/PortalBadge';
import { CURSOR_PORTAL, PORTAL_BTN, PORTAL_CHAT, PORTAL_TAB } from './arkorbit-ui/portalTheme';
import { ArkoCursorThreadSearch } from './portal/ArkoCursorComposer';
import {
  MemoryNudgeDialog,
  MemorySessionDialog,
  MemoryThumbDialog,
} from './portal/MemoryDialogs';
import {
  type MemcoreContext,
  type MemProofItem,
  type PinnedFact,
  addPinnedFact,
  buildChatTranscript,
  hatSpeicherbarenInhalt,
  makePinnedFact,
  pinnedFactToMemItem,
  removePinnedFact,
} from './portal/memcore';
import {
  buildDemoApprovalFristPayload,
  isDemoFristUrlEnabled,
  shouldAllowDemoFristCard,
} from './portal/verfahrensmotor/demoFristSeed';
import {
  isDocumentAnalyzeDone,
  clearDocumentAnalyzeGuard,
} from './portal/portalDocumentAnalyzeGuard';
import {
  ARKO_GUIDED_FLOW_EVENT,
  consumePendingGuidedFlow,
  type ArkoHubIntentDetail,
} from './portal/arkoHubModes';
import {
  guidedFlowTitle,
  isGuidedFlowId,
  startGuidedFlow,
  type GuidedFlowBotBlock,
  type GuidedFlowId,
  type GuidedFlowState,
  EMPTY_GUIDED_FLOW,
} from './portal/arkoGuidedFlows';
import type {
  PortalDocumentOpenFields,
  PortalOpenDocumentMeta,
  PortalSelectedDocumentPayload,
} from './portal/portalDocuments';
import { formatSynthesisDocumentLabels, PORTAL_MAX_OPEN_CHATS } from './portal/portalDocuments';
import { clearBackgroundJobStorage, shortDocumentLabel } from './portal/portalBackgroundJobs';
import { usePortalBackgroundJobs } from './portal/usePortalBackgroundJobs';

const STORAGE_PREFIX = 'arkorbit_portal_arko_threads_v1__';
const MAX_MESSAGES_PER_THREAD = 80;
const SAVE_DEBOUNCE_MS = 300;

interface Thread {
  id: string;
  title: string;
  messages: Message[];
  updatedAt: number;
  /** Pro Thread: Dokument-Kontext an/aus (persistiert). */
  documentContextEnabled?: boolean;
  /** true = Nutzer hat explizit umgeschaltet; View-Default darf nicht überschreiben. */
  documentContextUserOverride?: boolean;
  /** MEMCORE Modul B: Speichern-Nudge wurde für diesen Thread mind. 1× beantwortet (§7.7). */
  memNudgeDone?: boolean;
  /**
   * Stream 4: Vom Anwalt gepinnte Fall-Eckdaten (Mandant, Akte, Gegner). Hängen
   * bei jeder Anfrage dieses Threads automatisch als memContext am Body — spätere
   * Entwürfe werden vorbefüllt, ohne dass ARKO erneut nachfragt. Client-seitig
   * (localStorage, 1 Sitz = 1 Thread); roher PII-Text bleibt im Browser.
   */
  caseFacts?: PinnedFact[];
  /** Phase 1.3: Geführter Kachel-Flow (persistiert pro Thread). */
  guidedFlow?: GuidedFlowState;
}

/** MEMCORE Modul B (Labor): aktiviert Session-Speichern, Nudge + Daumen-Dialog. */
export interface ArkoMemcoreProps extends MemcoreContext {
  curatorUrl: string;
}

interface ArchivedThread extends Thread {
  archivedAt: number;
}

interface WorkspaceState {
  version: 1;
  activeThreadIds: string[];
  activeId: string;
  threads: Record<string, Thread>;
  archived: Record<string, ArchivedThread>;
}

/** Frontend-Abort für Kanzlei-ARKO: n8n/Ollama Labor ~50–56s; 90s Puffer (Donna nutzt 120s). */
export const PORTAL_ARKO_CHAT_TIMEOUT_MS = 90_000;

interface PortalArkoChatWorkspaceProps {
  customerId: string;
  webhookUrl: string;
  initialBotMessage: string;
  title: string;
  subtitle: string;
  accentColor?: 'blue' | 'purple' | 'amber';
  mode?: string;
  extraBody?: Record<string, unknown>;
  /** Default {@link PORTAL_ARKO_CHAT_TIMEOUT_MS} — InlineChat-Fallback 45s gilt nicht im Portal. */
  timeoutMs?: number;
  showQuickActions?: boolean;
  showRechtsfrageQuickAction?: boolean;
  /** Optionaler Dokument-/Editor-Kontext fürs Webhook-Body (n8n); null/undefined = weglassen. */
  documentContext?: string | null;
  /** Anzeigename des geöffneten Dokuments (Toggle-Hinweis). */
  documentDisplayName?: string | null;
  /** View-basierter Default für den Dokument-Kontext-Toggle (Parent setzt nach view). */
  defaultDocumentContextEnabled?: boolean;
  /** Split-View Dokument (Alpha): Pane-Header „ARKO“ reicht — kein zweiter InlineChat-Header. */
  hideChatHeader?: boolean;
  /** Schmale Cursor-artige Toolbar (Tabs + Kontext in 1–2 Zeilen). */
  compactChrome?: boolean;
  /** z. B. `border-0 shadow-none rounded-none` im Dokument-Split. */
  chatShellClassName?: string;
  /** Stream D: Vertragsgenerator-Slot-Filling im Chat aktivieren (Premium + Feature an). */
  enableContractIntent?: boolean;
  /** Ruft den Generate-Endpoint, lädt das Ergebnis in den Editor und liefert die Chat-Antwort. */
  onContractGenerate?: (args: {
    templateType: string;
    fields: Record<string, unknown>;
    displayName: string;
  }) => Promise<{ ok: boolean; message: string }>;
  /** Stream 3: Schreiben-/Schriftsatz-Entwurf als PDF exportieren (Gotenberg). */
  onExportSchreibenPdf?: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  onExportSchreibenDocx?: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  /** Datei-Karte im Chat → Portal-Viewer (OnlyOffice/PDF), kein Nextcloud-Tab. */
  onOpenPortalDocument?: (fields: PortalDocumentOpenFields) => void | Promise<void>;
  /** Slice 1.3b V2: Synthese-Entwurf ins geöffnete oder neues Dokument übernehmen. */
  onApplySynthesisToDocument?: (args: {
    text: string;
    target: 'existing' | 'new';
  }) => Promise<{ ok: boolean; message: string }>;
  /** Slice 1.3b V2: Dokument im Editor → beide Übernahme-Buttons im Chat. */
  synthesisHasOpenDocument?: boolean;
  /** Parent: letzter Chip/Intent für Startseite „Zuletzt“. */
  onActiveIntentChange?: (label: string | null) => void;
  /** MEMCORE Modul B (Labor): null/undefined = Gedächtnis-UI komplett aus. */
  memcore?: ArkoMemcoreProps | null;
  /** `cursor` = Agents-Layout mit Thread-Rail (ARKO-Tab); `compact` = Dokument-Split. */
  layoutVariant?: 'default' | 'cursor' | 'compact';
  cursorCustomerName?: string;
  cursorKanzlei?: string;
  /** Demo-Smoke `?demoFrist=1` — Mandanten-Pfad für Demo-Card in neuem Thread. */
  customerStatus?: string;
  customerEmail?: string;
  /** Extract-Fingerprint (Dokument + Revision + Kontextlänge) — Guard gegen Re-Analyse. */
  documentExtractFingerprint?: string | null;
  documentExtractRevision?: number;
  quellDokumentNcPath?: string | null;
  quellDokumentPbId?: string | null;
  akteNcPath?: string | null;
  /** Slice 1.3: Fokussiertes Dokument (Editor-Tab) — wechselt `documentContext`, kein neuer Chat-Thread. */
  activeDocumentId?: string | null;
  /** Slice 1.3: Metadaten aller offenen Editor-Tabs für Webhook `openDocuments[]`. */
  openDocumentsMeta?: PortalOpenDocumentMeta[];
  /** Slice 1.3b: Gewählte Docs für Synthese (2+ → Webhook `selectedDocuments[]`). */
  selectedDocuments?: PortalSelectedDocumentPayload[];
  /** Slice 1.3b: Anzeige „ARKO nutzt: A + B“. */
  synthesisDocumentLabels?: string | null;
  /** Nach Analyse-Upload: Dateien im Hub registrieren und im Editor öffnen. */
  onAnalyzeDocumentsReady?: (fields: import('./portal/portalDocuments').PortalDocumentOpenFields[]) => void | Promise<void>;
  /** Phase 1.3: PB-Token für Akten-Anlage in geführten Flows. */
  authToken?: string | null;
  /** Phase 1.4: Absatz-Index + cursorSelection für Webhook. */
  paragraphIndexMap?: import('./portal/portalParagraphMap').ParagraphIndexEntry[];
  cursorSelection?: import('./portal/portalParagraphMap').CursorSelection | null;
  onMarkDocumentAnalyzed?: () => void;
}

function storageKey(customerId: string): string {
  return `${STORAGE_PREFIX}${customerId}`;
}

/**
 * Loescht alle ARKO-Chats dieses Kunden aus dem Browser.
 * Nur fuer bewusste Voll-Wipe-Pfade — NICHT beim Logout (Verlauf bleibt
 * pro customerId in localStorage ueber Abmelden/Anmelden und Hard-Refresh).
 * Einzelne Threads loescht die UI via archive/delete.
 */
export function clearArkoChatStorage(customerId: string): void {
  try { localStorage.removeItem(storageKey(customerId)); } catch { /* noop */ }
  clearBackgroundJobStorage(customerId);
  clearDocumentAnalyzeGuard(customerId);
}

function newThreadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'thr_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function defaultThreadTitle(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `Chat ${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function hasDocumentContext(ctx: string | null | undefined): boolean {
  return ctx != null && ctx !== '';
}

function resolveDocumentContextEnabled(
  thread: Thread,
  defaultEnabled: boolean,
  docAvailable: boolean,
): boolean {
  if (!docAvailable) return false;
  if (thread.documentContextUserOverride && thread.documentContextEnabled !== undefined) {
    return thread.documentContextEnabled;
  }
  return defaultEnabled;
}

function deriveTitleFromMessages(messages: Message[], fallback: string): string {
  const firstUser = messages.find(m => m.role === 'user');
  if (firstUser && firstUser.text.trim()) {
    const trimmed = firstUser.text.trim().replace(/\s+/g, ' ');
    return trimmed.length > 40 ? trimmed.slice(0, 40) + '…' : trimmed;
  }
  return fallback;
}

function threadHasDemoFristCard(messages: Message[]): boolean {
  return messages.some(
    (m) => m.messageKind === 'approval_frist' || Boolean(m.approvalFrist),
  );
}

/** Labor `?demoFrist=1`: Demo-Card erst nach User-Nachricht + Bot-Antwort (nicht beim Thread-Start). */
function maybeAppendDemoFristCard(
  messages: Message[],
  customerStatus?: string,
  customerEmail?: string,
  documentContext?: string | null,
): Message[] {
  if (!shouldAllowDemoFristCard(documentContext) || threadHasDemoFristCard(messages)) return messages;

  const userCount = messages.filter((m) => m.role === 'user').length;
  if (userCount < 1) return messages;

  const lastUserIdx = messages.map((m) => m.role).lastIndexOf('user');
  const hasBotReplyAfterUser = messages
    .slice(lastUserIdx + 1)
    .some((m) => m.role === 'bot' && m.messageKind !== 'approval_frist');
  if (!hasBotReplyAfterUser) return messages;

  const payload = buildDemoApprovalFristPayload(customerStatus, customerEmail);
  return [
    ...messages,
    {
      role: 'bot',
      text: 'Ich habe eine Berufungsfrist erkannt (Demo-Smoke). Bitte bestätigen Sie den Vorschlag.',
      messageKind: 'approval_frist',
      approvalFrist: payload,
      id: newMessageId(),
    },
  ];
}

function guidedBlocksToMessages(blocks: GuidedFlowBotBlock[]): Message[] {
  return blocks.map((block) => ({
    role: 'bot' as const,
    text: block.text,
    id: newMessageId(),
    messageKind: 'guided_flow' as const,
    ...(block.chips?.length ? { guidedFlowChips: block.chips } : {}),
    ...(block.riskFindings?.length ? { guidedRiskFindings: block.riskFindings } : {}),
    ...(block.laborDemo ? { guidedLaborDemo: true } : {}),
    ...(block.mandatsConfirm ? { mandatsConfirm: block.mandatsConfirm } : {}),
  }));
}

function makeFreshThread(): Thread {
  return {
    id: newThreadId(),
    title: defaultThreadTitle(),
    messages: [],
    updatedAt: Date.now(),
  };
}

/** Neuer Thread mit geführtem Kachel-Flow (immer dediziert, nie bestehenden Chat wiederverwenden). */
function buildGuidedFlowThreadPatch(
  modeId: GuidedFlowId,
  docAvailable: boolean,
  defaultDocumentContextEnabled: boolean,
): { thread: Thread; warning?: string } | null {
  const flowResult = startGuidedFlow(modeId);
  const t: Thread = {
    ...makeFreshThread(),
    title: guidedFlowTitle(modeId),
    messages: guidedBlocksToMessages(flowResult.blocks),
    guidedFlow: flowResult.state,
    documentContextEnabled: docAvailable ? defaultDocumentContextEnabled : false,
    updatedAt: Date.now(),
  };
  return { thread: t };
}

function isWorkspaceState(value: unknown): value is WorkspaceState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<WorkspaceState>;
  return v.version === 1
    && Array.isArray(v.activeThreadIds)
    && typeof v.activeId === 'string'
    && !!v.threads
    && typeof v.threads === 'object';
}

function loadState(
  customerId: string,
  initialBotMessage: string,
  customerStatus?: string,
  customerEmail?: string,
): WorkspaceState {
  try {
    const raw = localStorage.getItem(storageKey(customerId));
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isWorkspaceState(parsed) && parsed.activeThreadIds.length > 0) {
        return {
          ...parsed,
          archived: parsed.archived ?? {},
        };
      }
    }
  } catch { /* fall through to fresh state */ }
  const fresh = makeFreshThread();
  return {
    version: 1,
    activeThreadIds: [fresh.id],
    activeId: fresh.id,
    threads: { [fresh.id]: fresh },
    archived: {},
  };
}

function trimMessages(msgs: Message[]): Message[] {
  if (msgs.length <= MAX_MESSAGES_PER_THREAD) return msgs;
  const head = msgs.length > 0 && msgs[0].role === 'bot' ? [msgs[0]] : [];
  const tail = msgs.slice(-(MAX_MESSAGES_PER_THREAD - head.length));
  return [...head, ...tail];
}

const PortalArkoChatWorkspace: React.FC<PortalArkoChatWorkspaceProps> = ({
  customerId,
  webhookUrl,
  initialBotMessage,
  title,
  subtitle,
  accentColor = 'blue',
  mode,
  extraBody,
  timeoutMs = PORTAL_ARKO_CHAT_TIMEOUT_MS,
  showQuickActions,
  showRechtsfrageQuickAction,
  documentContext,
  documentDisplayName,
  defaultDocumentContextEnabled = false,
  hideChatHeader = false,
  compactChrome = false,
  chatShellClassName = PORTAL_CHAT.shellInner,
  enableContractIntent,
  onContractGenerate,
  onExportSchreibenPdf,
  onExportSchreibenDocx,
  onOpenPortalDocument,
  onApplySynthesisToDocument,
  synthesisHasOpenDocument = false,
  onActiveIntentChange: onActiveIntentChangeParent,
  memcore = null,
  layoutVariant = 'default',
  cursorCustomerName,
  cursorKanzlei,
  customerStatus,
  customerEmail,
  documentExtractFingerprint,
  documentExtractRevision = 0,
  quellDokumentNcPath,
  quellDokumentPbId,
  akteNcPath,
  activeDocumentId,
  openDocumentsMeta,
  selectedDocuments,
  synthesisDocumentLabels,
  onAnalyzeDocumentsReady,
  authToken,
  paragraphIndexMap,
  cursorSelection,
  onMarkDocumentAnalyzed,
}) => {
  const isCursorLayout = layoutVariant === 'cursor';
  const isCompactLayout = layoutVariant === 'compact' || compactChrome;
  const docAvailable = hasDocumentContext(documentContext);
  const [state, setState] = useState<WorkspaceState>(() =>
    loadState(customerId, initialBotMessage, customerStatus, customerEmail),
  );
  const [showArchive, setShowArchive] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [threadSearch, setThreadSearch] = useState('');
  const [activeIntentLabel, setActiveIntentLabel] = useState<string | null>(null);
  const saveTimer = useRef<number | null>(null);
  // ── MEMCORE Modul B: Dialog-Zustände ───────────────────────────────────────
  /** Session-Dialog; pendingAction = ursprünglich gewünschte Beendigung (Nudge-Pfad). */
  const [memSession, setMemSession] = useState<
    { threadId: string; pendingAction: 'archive' | 'delete' | null } | null
  >(null);
  const [memNudge, setMemNudge] = useState<{ threadId: string; action: 'archive' | 'delete' } | null>(null);
  const [memThumb, setMemThumb] = useState<{ messageId: string; text: string } | null>(null);
  /** Stream 4: Fall-Gedächtnis-Panel (gepinnte Eckdaten ansehen/entfernen) auf/zu. */
  const [showCaseFacts, setShowCaseFacts] = useState(false);

  const handleActiveIntentChange = useCallback(
    (label: string | null) => {
      setActiveIntentLabel(label);
      onActiveIntentChangeParent?.(label);
    },
    [onActiveIntentChangeParent],
  );

  // Defensiv: Wenn der Kunde im selben Tab wechselt (sehr selten), neu laden.
  useEffect(() => {
    setState(loadState(customerId, initialBotMessage, customerStatus, customerEmail));
    setShowArchive(false);
    setWarning(null);
    // initialBotMessage absichtlich nicht in deps — neuer Begruessungstext darf bestehende Threads nicht ueberschreiben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  /** Phase 1.3: Kachel-Klick → immer neuer Thread mit geführtem Flow. */
  const startGuidedFlowThread = useCallback(
    (modeId: GuidedFlowId) => {
      const patch = buildGuidedFlowThreadPatch(modeId, docAvailable, defaultDocumentContextEnabled);
      if (!patch) return;
      setState((prev) => {
        if (prev.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS) {
          setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats. Bitte einen archivieren oder löschen.`);
          return prev;
        }
        setWarning(null);
        const t = patch.thread;
        return {
          ...prev,
          activeThreadIds: [...prev.activeThreadIds, t.id],
          activeId: t.id,
          threads: { ...prev.threads, [t.id]: t },
        };
      });
    },
    [defaultDocumentContextEnabled, docAvailable],
  );

  useEffect(() => {
    const pending = consumePendingGuidedFlow();
    if (pending && isGuidedFlowId(pending)) {
      startGuidedFlowThread(pending);
    }
    const onGuidedFlow = (e: Event) => {
      const detail = (e as CustomEvent<ArkoHubIntentDetail>).detail;
      if (!detail?.modeId || !isGuidedFlowId(detail.modeId)) return;
      startGuidedFlowThread(detail.modeId);
    };
    window.addEventListener(ARKO_GUIDED_FLOW_EVENT, onGuidedFlow);
    return () => window.removeEventListener(ARKO_GUIDED_FLOW_EVENT, onGuidedFlow);
  }, [startGuidedFlowThread]);

  // View-Wechsel: Default übernehmen, sofern der Nutzer nicht explizit überschrieben hat.
  useEffect(() => {
    if (!docAvailable) return;
    setState(prev => {
      let changed = false;
      const newThreads = { ...prev.threads };
      for (const [id, thread] of Object.entries(prev.threads)) {
        if (thread.documentContextUserOverride) continue;
        if (thread.documentContextEnabled !== defaultDocumentContextEnabled) {
          newThreads[id] = { ...thread, documentContextEnabled: defaultDocumentContextEnabled };
          changed = true;
        }
      }
      return changed ? { ...prev, threads: newThreads } : prev;
    });
  }, [defaultDocumentContextEnabled, docAvailable]);

  useEffect(() => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      try {
        localStorage.setItem(storageKey(customerId), JSON.stringify(state));
      } catch { /* Quota oder privater Modus — bewusst still */ }
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [state, customerId]);

  /** Fabrik fuer Thread-spezifische Setter. Wichtig: bindet an `threadId`, nicht an `activeId` —
   *  damit eine Antwort, die nach Tab-Wechsel eintrifft, nicht im falschen Tab landet. */
  const makeThreadSetter = useCallback(
    (threadId: string): React.Dispatch<React.SetStateAction<Message[]>> =>
      (value) => {
        setState(prev => {
          const current = prev.threads[threadId];
          if (!current) return prev;
          const next = typeof value === 'function'
            ? (value as (p: Message[]) => Message[])(current.messages)
            : value;
          const docCtxEnabled = resolveDocumentContextEnabled(
            current,
            defaultDocumentContextEnabled,
            docAvailable,
          );
          const withDemo = maybeAppendDemoFristCard(
            next,
            customerStatus,
            customerEmail,
            docCtxEnabled ? documentContext : null,
          );
          const trimmed = trimMessages(withDemo);
          const updated: Thread = {
            ...current,
            messages: trimmed,
            updatedAt: Date.now(),
            title: deriveTitleFromMessages(trimmed, current.title),
          };
          return { ...prev, threads: { ...prev.threads, [threadId]: updated } };
        });
      },
    [customerEmail, customerStatus, documentContext, docAvailable, defaultDocumentContextEnabled],
  );

  const getThreadMessages = useCallback(
    (threadId: string) => state.threads[threadId]?.messages,
    [state.threads],
  );

  const appendToThread = useCallback(
    (threadId: string, updater: (prev: Message[]) => Message[]) => {
      makeThreadSetter(threadId)(updater);
    },
    [makeThreadSetter],
  );

  const {
    pendingJobs,
    completionNotices,
    canEnqueue,
    hasRunningJobs,
    enqueueJob,
    cancelJob,
    dismissNotice,
  } = usePortalBackgroundJobs({
    customerId,
    webhookUrl,
    timeoutMs,
    mode,
    extraBody,
    getThreadMessages,
    appendToThread,
  });

  const activeThread = state.threads[state.activeId];
  const activeMessagesSetter = useMemo(
    () => makeThreadSetter(activeThread?.id ?? ''),
    [activeThread?.id, makeThreadSetter],
  );

  // ── Stream 4: Fall-Eckdaten (gepinnte Thread-Fakten, Auto-Vorbefüllung) ─────
  const activeCaseFacts = activeThread?.caseFacts ?? [];
  /** Gepinnte Fakten des aktiven Threads als memContext (stabil pro Inhalt). */
  const activePinnedMemContext = useMemo<MemProofItem[]>(
    () => (memcore ? activeCaseFacts.map(pinnedFactToMemItem) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memcore, JSON.stringify(activeCaseFacts)],
  );

  const pinFactToActiveThread = useCallback(
    (text: string) => {
      if (!memcore || !activeThread) return;
      const fact = makePinnedFact(text);
      if (!fact) return;
      const threadId = activeThread.id;
      setState(prev => {
        const t = prev.threads[threadId];
        if (!t) return prev;
        const nextFacts = addPinnedFact(t.caseFacts ?? [], fact);
        if (nextFacts === (t.caseFacts ?? [])) return prev;
        return { ...prev, threads: { ...prev.threads, [threadId]: { ...t, caseFacts: nextFacts } } };
      });
    },
    [memcore, activeThread],
  );

  const removeCaseFact = useCallback(
    (factId: string) => {
      if (!activeThread) return;
      const threadId = activeThread.id;
      setState(prev => {
        const t = prev.threads[threadId];
        if (!t || !t.caseFacts) return prev;
        return { ...prev, threads: { ...prev.threads, [threadId]: { ...t, caseFacts: removePinnedFact(t.caseFacts, factId) } } };
      });
    },
    [activeThread],
  );

  const handleGuidedFlowStateChange = useCallback((next: GuidedFlowState) => {
    if (!activeThread) return;
    const threadId = activeThread.id;
    setState((prev) => {
      const t = prev.threads[threadId];
      if (!t) return prev;
      return {
        ...prev,
        threads: {
          ...prev.threads,
          [threadId]: {
            ...t,
            guidedFlow: next.active ? next : undefined,
            updatedAt: Date.now(),
          },
        },
      };
    });
  }, [activeThread]);

  const newChat = () => {
    if (state.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS) {
      setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats. Bitte einen archivieren oder löschen, dann neu starten.`);
      return;
    }
    setState(prev => {
      const t: Thread = {
        ...makeFreshThread(),
        documentContextEnabled: docAvailable ? defaultDocumentContextEnabled : false,
      };
      return {
        ...prev,
        activeThreadIds: [...prev.activeThreadIds, t.id],
        activeId: t.id,
        threads: { ...prev.threads, [t.id]: t },
      };
    });
    setWarning(null);
  };

  const switchTo = (id: string) => {
    setState(prev => prev.activeId === id ? prev : { ...prev, activeId: id });
    setWarning(null);
  };

  // ── MEMCORE Modul B: Nudge-Logik (mindestens 1× fragen, kein stilles Speichern §7.7) ──

  const markMemNudgeDone = useCallback((threadId: string) => {
    setState(prev => {
      const t = prev.threads[threadId];
      if (!t || t.memNudgeDone) return prev;
      return { ...prev, threads: { ...prev.threads, [threadId]: { ...t, memNudgeDone: true } } };
    });
  }, []);

  /** Nudge nötig? Nur bei aktivem MEMCORE, speicherbarem Inhalt und noch nie gefragt. */
  const memNudgeNoetig = useCallback(
    (threadId: string): boolean => {
      if (!memcore) return false;
      const t = state.threads[threadId];
      return Boolean(t && !t.memNudgeDone && hatSpeicherbarenInhalt(t.messages));
    },
    [memcore, state.threads],
  );

  // Neuladen/Schließen des Tabs: generischer Browser-Prompt als Mindest-Nudge,
  // solange ein aktiver Thread speicherbaren Inhalt hat und noch nie gefragt wurde.
  useEffect(() => {
    if (!memcore) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const offen = state.activeThreadIds.some(id => {
        const t = state.threads[id];
        return t && !t.memNudgeDone && hatSpeicherbarenInhalt(t.messages);
      });
      if (offen) {
        e.preventDefault();
        // Chromium verlangt returnValue für den Dialog
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [memcore, state.activeThreadIds, state.threads]);

  const archiveThread = (id: string) => {
    if (memNudgeNoetig(id)) {
      setMemNudge({ threadId: id, action: 'archive' });
      return;
    }
    archiveThreadNow(id);
  };

  const archiveThreadNow = (id: string) => {
    setState(prev => {
      const t = prev.threads[id];
      if (!t) return prev;
      const remainingActive = prev.activeThreadIds.filter(x => x !== id);
      const newThreads = { ...prev.threads };
      delete newThreads[id];
      const archived: ArchivedThread = { ...t, archivedAt: Date.now() };
      if (remainingActive.length === 0) {
        const fresh = makeFreshThread();
        return {
          ...prev,
          activeThreadIds: [fresh.id],
          activeId: fresh.id,
          threads: { [fresh.id]: fresh },
          archived: { ...prev.archived, [id]: archived },
        };
      }
      const newActive = prev.activeId === id ? remainingActive[remainingActive.length - 1] : prev.activeId;
      return {
        ...prev,
        activeThreadIds: remainingActive,
        activeId: newActive,
        threads: newThreads,
        archived: { ...prev.archived, [id]: archived },
      };
    });
    setWarning(null);
  };

  const deleteThread = (id: string) => {
    if (memNudgeNoetig(id)) {
      setMemNudge({ threadId: id, action: 'delete' });
      return;
    }
    deleteThreadNow(id);
  };

  const deleteThreadNow = (id: string) => {
    if (!window.confirm('Diesen Chat endgültig löschen?')) return;
    setState(prev => {
      const remainingActive = prev.activeThreadIds.filter(x => x !== id);
      const newThreads = { ...prev.threads };
      delete newThreads[id];
      if (remainingActive.length === 0) {
        const fresh = makeFreshThread();
        return {
          ...prev,
          activeThreadIds: [fresh.id],
          activeId: fresh.id,
          threads: { [fresh.id]: fresh },
        };
      }
      const newActive = prev.activeId === id ? remainingActive[remainingActive.length - 1] : prev.activeId;
      return {
        ...prev,
        activeThreadIds: remainingActive,
        activeId: newActive,
        threads: newThreads,
      };
    });
    setWarning(null);
  };

  const restoreFromArchive = (id: string) => {
    if (state.activeThreadIds.length >= PORTAL_MAX_OPEN_CHATS) {
      setWarning(`Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats. Bitte erst einen archivieren oder löschen.`);
      return;
    }
    setState(prev => {
      const arch = prev.archived[id];
      if (!arch) return prev;
      const newArchived = { ...prev.archived };
      delete newArchived[id];
      const restored: Thread = {
        id: arch.id,
        title: arch.title,
        messages: arch.messages,
        updatedAt: Date.now(),
        caseFacts: arch.caseFacts,
      };
      return {
        ...prev,
        activeThreadIds: [...prev.activeThreadIds, restored.id],
        activeId: restored.id,
        threads: { ...prev.threads, [restored.id]: restored },
        archived: newArchived,
      };
    });
    setWarning(null);
  };

  const deleteArchived = (id: string) => {
    if (!window.confirm('Diesen archivierten Chat endgültig löschen?')) return;
    setState(prev => {
      const newArchived = { ...prev.archived };
      delete newArchived[id];
      return { ...prev, archived: newArchived };
    });
  };

  const archivedList = useMemo(
    () => Object.values(state.archived).sort((a, b) => b.archivedAt - a.archivedAt),
    [state.archived],
  );

  const activeTabs = state.activeThreadIds
    .map(id => state.threads[id])
    .filter((t): t is Thread => Boolean(t));
  const filteredActiveTabs = useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    if (!q) return activeTabs;
    return activeTabs.filter((t) => t.title.toLowerCase().includes(q));
  }, [activeTabs, threadSearch]);
  const fullActive = activeTabs.length >= PORTAL_MAX_OPEN_CHATS;

  const activeDocumentContextEnabled = activeThread
    ? resolveDocumentContextEnabled(activeThread, defaultDocumentContextEnabled, docAvailable)
    : false;

  const documentAnalyzeDone = useMemo(
    () => isDocumentAnalyzeDone(customerId, documentExtractFingerprint ?? null),
    [customerId, documentExtractFingerprint],
  );

  const prevActiveDocumentIdRef = useRef<string | null>(null);

  // Slice 1.3: Dokument-Fokus wechselt → Hinweis im globalen Chat, kein neuer Thread.
  useEffect(() => {
    const docId = activeDocumentId?.trim() || null;
    const prev = prevActiveDocumentIdRef.current;
    prevActiveDocumentIdRef.current = docId;

    if (!docId || !docAvailable || !activeThread) return;
    if (!prev || prev === docId) return;

    const name = documentDisplayName?.trim() || 'Dokument';
    const threadId = activeThread.id;
    setState((prevState) => {
      const t = prevState.threads[threadId];
      if (!t) return prevState;
      const hintText = `ARKO arbeitet jetzt mit: ${name}`;
      const last = t.messages[t.messages.length - 1];
      if (last?.role === 'bot' && last.text === hintText) return prevState;
      const messages = trimMessages([
        ...t.messages,
        {
          role: 'bot' as const,
          text: hintText,
          messageKind: 'document_focus_switch' as const,
          id: newMessageId(),
        },
      ]);
      return {
        ...prevState,
        threads: {
          ...prevState.threads,
          [threadId]: { ...t, messages, updatedAt: Date.now() },
        },
      };
    });
  }, [activeDocumentId, docAvailable, documentDisplayName, activeThread]);

  // Keine stille Frist-Probe mehr beim Dokument-Öffnen — nur noch explizit per Chip/Intent.

  const startBackgroundDocumentAnalysis = useCallback(() => {
    if (!canEnqueue || !activeThread) return;

    const checkedDocs = selectedDocuments?.filter((d) => d.documentContext?.trim()) ?? [];
    const useMultiSelect = checkedDocs.length >= 1;

    if (useMultiSelect) {
      const labels = formatSynthesisDocumentLabels(checkedDocs.map((d) => d.title))
        ?? checkedDocs.map((d) => d.title).join(' + ');
      const combinedContext = checkedDocs
        .map((d) => `--- ${d.title} ---\n${d.documentContext.trim()}`)
        .join('\n\n');
      const primary = checkedDocs.find((d) => d.documentId === activeDocumentId?.trim())
        ?? checkedDocs[0];

      enqueueJob({
        documentId: primary.documentId,
        documentName: labels,
        threadId: activeThread.id,
        history: activeThread.messages,
        documentContext: combinedContext,
        openDocumentsMeta,
        selectedDocuments: checkedDocs,
        documentExtractFingerprint: primary.documentExtractFingerprint ?? documentExtractFingerprint,
      });
      return;
    }

    if (!activeDocumentId?.trim() || !documentContext?.trim()) return;

    enqueueJob({
      documentId: activeDocumentId.trim(),
      documentName: documentDisplayName?.trim() || 'Dokument',
      threadId: activeThread.id,
      history: activeThread.messages,
      documentContext,
      openDocumentsMeta,
      documentExtractFingerprint,
    });
  }, [
    canEnqueue,
    activeThread,
    selectedDocuments,
    activeDocumentId,
    documentContext,
    documentDisplayName,
    openDocumentsMeta,
    documentExtractFingerprint,
    enqueueJob,
  ]);

  const backgroundAnalysisReady = useMemo(() => {
    const checkedDocs = selectedDocuments?.filter((d) => d.documentContext?.trim()) ?? [];
    if (checkedDocs.length >= 1) return true;
    return Boolean(activeDocumentId?.trim() && documentContext?.trim());
  }, [selectedDocuments, activeDocumentId, documentContext]);

  const backgroundAnalysisLabel = useMemo(() => {
    if (hasRunningJobs) return 'Analyse läuft…';
    const checkedCount = selectedDocuments?.filter((d) => d.documentContext?.trim()).length ?? 0;
    if (checkedCount >= 2) return 'Ausgewählte im Hintergrund analysieren';
    if (checkedCount === 1) return 'Ausgewähltes im Hintergrund analysieren';
    return 'Im Hintergrund analysieren';
  }, [selectedDocuments, hasRunningJobs]);

  const backgroundAnalysisTitle = useMemo(() => {
    if (!canEnqueue) return 'Maximal zwei Hintergrundanalysen gleichzeitig';
    const checkedDocs = selectedDocuments?.filter((d) => d.documentContext?.trim()) ?? [];
    if (checkedDocs.length >= 1) {
      const labels = formatSynthesisDocumentLabels(checkedDocs.map((d) => d.title))
        ?? checkedDocs.map((d) => d.title).join(' + ');
      return `„${labels}“ im Hintergrund analysieren`;
    }
    if (documentContext?.trim()) {
      return `„${documentDisplayName?.trim() || 'Dokument'}“ im Hintergrund analysieren`;
    }
    return 'Dokumenttext wird noch geladen';
  }, [canEnqueue, selectedDocuments, documentContext, documentDisplayName]);

  const toggleDocumentContext = (enabled: boolean) => {
    if (!activeThread || !docAvailable) return;
    setState(prev => {
      const t = prev.threads[activeThread.id];
      if (!t) return prev;
      const wasEnabled = resolveDocumentContextEnabled(t, defaultDocumentContextEnabled, docAvailable);
      let messages = t.messages;
      if (enabled && !wasEnabled) {
        const name = (documentDisplayName?.trim()) || 'Dokument';
        messages = trimMessages([
          ...t.messages,
          { role: 'bot' as const, text: `Ich beziehe mich ab jetzt auf ‚${name}'.` },
        ]);
      }
      const updated: Thread = {
        ...t,
        messages,
        documentContextEnabled: enabled,
        documentContextUserOverride: true,
        updatedAt: Date.now(),
        title: deriveTitleFromMessages(messages, t.title),
      };
      return { ...prev, threads: { ...prev.threads, [t.id]: updated } };
    });
  };

  const tabBtnClass = isCompactLayout
    ? 'px-1.5 py-0 rounded-md text-[10px] max-w-[140px]'
    : isCursorLayout
      ? 'px-2.5 py-2 rounded-lg text-xs w-full max-w-none justify-start'
      : 'px-3 py-1.5 rounded-lg text-xs max-w-[220px]';
  const tabIconClass = isCompactLayout ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5';

  const renderThreadTabActions = (t: Thread, isActive: boolean) => (
    <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          archiveThread(t.id);
        }}
        className="p-1 rounded hover:bg-amber-500/15 text-[#6c706c] hover:text-amber-300"
        title="Chat archivieren"
        aria-label="Chat archivieren"
      >
        <Archive className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          deleteThread(t.id);
        }}
        className="p-1 rounded hover:bg-rose-500/15 text-[#6c706c] hover:text-rose-300"
        title="Chat löschen"
        aria-label="Chat löschen"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      {memcore ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!hatSpeicherbarenInhalt(t.messages)) return;
            if (t.id !== state.activeId) switchTo(t.id);
            setMemSession({ threadId: t.id, pendingAction: 'archive' });
          }}
          disabled={!hatSpeicherbarenInhalt(t.messages)}
          className="p-1 rounded hover:bg-[#57c06a]/15 text-[#6c706c] hover:text-[#6fcb7a] disabled:opacity-30 disabled:cursor-not-allowed"
          title="Session beenden — Merkpunkte prüfen und speichern"
          aria-label="Session beenden und speichern"
        >
          <BookMarked className="w-3.5 h-3.5" />
        </button>
      ) : null}
      {memcore ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (t.id !== state.activeId) switchTo(t.id);
            setShowCaseFacts((s) => (isActive ? !s : true));
          }}
          className={`p-1 rounded hover:bg-[#57c06a]/15 ${
            isActive && showCaseFacts ? 'text-[#6fcb7a]' : 'text-[#6c706c] hover:text-[#6fcb7a]'
          }`}
          title="Fall-Gedächtnis — gemerkte Eckdaten dieses Chats"
          aria-label="Fall-Gedächtnis anzeigen"
        >
          <Brain className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );

  const renderThreadTabs = (layout: 'default' | 'cursorTop' = 'default') => {
    const tabs = layout === 'cursorTop' ? filteredActiveTabs : activeTabs;
    return tabs.map((t) => {
      const isActive = t.id === state.activeId;
      if (layout === 'cursorTop') {
        return (
          <div
            key={t.id}
            className={`group flex items-center flex-shrink-0 max-w-[min(100%,260px)] rounded-lg border transition-colors ${
              isActive
                ? 'border-[#57c06a]/35 bg-[#57c06a]/10 text-[#e6e7e5]'
                : 'border-[#2b2f2b] bg-[#141715]/60 text-[#9aa09a] hover:border-[#57c06a]/20 hover:text-[#e6e7e5]'
            }`}
          >
            <button
              type="button"
              onClick={() => switchTo(t.id)}
              className="truncate flex-1 text-left px-3 py-1.5 text-xs font-medium min-w-0"
              title={t.title}
            >
              {t.title}
            </button>
            {renderThreadTabActions(t, isActive)}
          </div>
        );
      }
      return (
        <div
          key={t.id}
          role="button"
          tabIndex={0}
          onClick={() => switchTo(t.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') switchTo(t.id);
          }}
          title={t.title}
          className={`group ${PORTAL_TAB.base} ${tabBtnClass} ${
            isActive ? PORTAL_TAB.active : PORTAL_TAB.idle
          }`}
        >
          <MessageSquare
            className={`${tabIconClass} flex-shrink-0 ${isActive ? PORTAL_TAB.iconActive : PORTAL_TAB.iconIdle}`}
          />
          <span className="truncate flex-1 text-left">{t.title}</span>
          <div className="flex items-center gap-0.5 ml-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                archiveThread(t.id);
              }}
              className="p-0.5 rounded hover:bg-slate-700/60 text-slate-400 hover:text-amber-300"
              title="Archivieren"
              aria-label="Chat archivieren"
            >
              <Archive className="w-2.5 h-2.5" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                deleteThread(t.id);
              }}
              className="p-0.5 rounded hover:bg-slate-700/60 text-slate-400 hover:text-rose-300"
              title="Löschen"
              aria-label="Chat löschen"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      );
    });
  };

  const renderCursorTopBar = () => (
    <header className="arko-cursor-topbar flex-shrink-0 sticky top-0 z-20 border-b border-white/[0.06] bg-slate-900/95 backdrop-blur-md">
      <div className="flex items-center gap-2 px-3 py-2 min-h-[44px]">
        <button
          type="button"
          onClick={newChat}
          disabled={fullActive}
          className={`flex items-center justify-center rounded-lg border border-white/[0.08] p-2 text-slate-200 hover:border-violet-500/30 hover:bg-violet-500/5 transition-colors flex-shrink-0 ${
            fullActive ? 'opacity-40 cursor-not-allowed' : ''
          }`}
          title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Neuer Chat'}
          aria-label="Neuer Chat"
        >
          <Plus className="w-4 h-4" />
        </button>
        <div className="flex-1 flex items-center gap-1.5 overflow-x-auto custom-scrollbar min-w-0 py-0.5">
          {filteredActiveTabs.length === 0 ? (
            <span className="text-xs text-slate-500 px-2 whitespace-nowrap">Noch keine Chats</span>
          ) : (
            renderThreadTabs('cursorTop')
          )}
        </div>
        <div className="hidden sm:block w-[min(100%,200px)] flex-shrink-0">
          <ArkoCursorThreadSearch value={threadSearch} onChange={setThreadSearch} />
        </div>
        {archivedList.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowArchive((s) => !s)}
            className="p-2 rounded-lg border border-white/[0.08] text-slate-400 hover:text-violet-200 hover:border-violet-500/30 hover:bg-violet-500/5 flex-shrink-0"
            title={`Archiv (${archivedList.length})`}
            aria-label={`Archiv (${archivedList.length})`}
            aria-expanded={showArchive}
          >
            <Archive className="w-4 h-4" />
          </button>
        ) : null}
      </div>
    </header>
  );

  const renderNewChatButton = (cursorRail = false) => (
    <button
      type="button"
      onClick={newChat}
      disabled={fullActive}
      className={`flex items-center gap-0.5 flex-shrink-0 ${
        cursorRail
          ? CURSOR_PORTAL.railBtn
          : isCompactLayout
            ? 'px-1 py-0 rounded-md text-[10px]'
            : 'px-2.5 py-1.5 rounded-lg text-xs'
      } ${
        fullActive && !cursorRail ? PORTAL_BTN.secondaryDisabled : cursorRail ? '' : PORTAL_BTN.secondary
      } ${fullActive && cursorRail ? 'opacity-40 cursor-not-allowed' : ''}`}
      title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Neuer Chat'}
    >
      <Plus className={isCompactLayout ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {!isCompactLayout && (cursorRail ? ' Neuer Chat' : ' Neuer Chat')}
    </button>
  );

  /** MEMCORE Modul B: „Session beenden“ — Vorschau-Speichern + Archivieren (Variante A). */
  const renderSessionEndButton = () => {
    if (!memcore || !activeThread) return null;
    const speicherbar = hatSpeicherbarenInhalt(activeThread.messages);
    return (
      <button
        type="button"
        onClick={() => setMemSession({ threadId: activeThread.id, pendingAction: 'archive' })}
        disabled={!speicherbar}
        className={`flex items-center gap-0.5 flex-shrink-0 ${
          compactChrome ? 'px-1 py-0 rounded-md text-[10px]' : 'px-2.5 py-1.5 rounded-lg text-xs'
        } ${speicherbar ? PORTAL_BTN.secondary : PORTAL_BTN.secondaryDisabled}`}
        title={
          speicherbar
            ? 'Session beenden — Merkpunkte prüfen und speichern'
            : 'Session beenden (noch kein speicherbarer Inhalt)'
        }
      >
        <BookMarked className={compactChrome ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {!compactChrome && ' Session beenden'}
      </button>
    );
  };

  /** Stream 4: „Fall-Gedächtnis (n)“ — gepinnte Eckdaten ansehen/entfernen. */
  const renderCaseFactsButton = () => {
    if (!memcore) return null;
    const n = activeCaseFacts.length;
    return (
      <button
        type="button"
        onClick={() => setShowCaseFacts(s => !s)}
        disabled={n === 0}
        className={`flex items-center gap-0.5 flex-shrink-0 ${
          compactChrome ? 'px-1 py-0 rounded-md text-[10px]' : 'px-2.5 py-1.5 rounded-lg text-xs'
        } ${n > 0 ? PORTAL_BTN.secondary : PORTAL_BTN.secondaryDisabled}`}
        aria-expanded={showCaseFacts}
        title={
          n > 0
            ? 'Fall-Gedächtnis: gemerkte Eckdaten dieses Chats (werden in Entwürfen vorbefüllt)'
            : 'Noch keine Fall-Eckdaten gemerkt — unter einer eigenen Nachricht „Merken“ wählen'
        }
      >
        <Brain className={compactChrome ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {compactChrome ? n : `Fall-Gedächtnis${n > 0 ? ` (${n})` : ''}`}
        {n > 0 ? (showCaseFacts ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />) : null}
      </button>
    );
  };

  const renderArchiveToggle = () =>
    archivedList.length > 0 ? (
      <button
        type="button"
        onClick={() => setShowArchive(s => !s)}
        className={`flex items-center gap-0.5 flex-shrink-0 ${
          compactChrome
            ? 'px-1 py-0 rounded-md text-[10px]'
            : 'px-2.5 py-1.5 rounded-lg text-xs ml-auto'
        } ${PORTAL_BTN.secondary}`}
        aria-expanded={showArchive}
        title={`Archiv (${archivedList.length})`}
      >
        <Archive className={compactChrome ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {compactChrome ? archivedList.length : `Archiv (${archivedList.length})`}
        {showArchive ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
      </button>
    ) : null;

  const renderDocumentContextToggle = (inline = false) => {
    if (!docAvailable && !inline) {
      return compactChrome ? null : (
        <span className="text-[10px] text-slate-500 italic">Kein Dokument geöffnet</span>
      );
    }
    if (!docAvailable) return null;
    return (
      <label
        className={`flex items-center gap-1 cursor-pointer select-none flex-shrink-0 ${
          inline ? '' : 'justify-end'
        }`}
        title={
          documentDisplayName?.trim()
            ? `Dokument-Kontext für „${documentDisplayName.trim()}“`
            : 'Dokument-Kontext ein- oder ausschalten'
        }
      >
        <span className="text-[10px] text-slate-500 whitespace-nowrap">
          {compactChrome ? 'Dok.' : 'Dokument-Kontext'}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={activeDocumentContextEnabled}
          aria-label="Dokument-Kontext ein- oder ausschalten"
          onClick={() => toggleDocumentContext(!activeDocumentContextEnabled)}
          className={`relative inline-flex flex-shrink-0 items-center rounded-full transition-colors ${
            compactChrome ? 'h-4 w-7' : 'h-5 w-9'
          } ${activeDocumentContextEnabled ? PORTAL_CHAT.toggleOn : PORTAL_CHAT.toggleOff}`}
        >
          <span
            className={`inline-block rounded-full bg-white shadow transition-transform ${
              compactChrome ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
            } ${
              activeDocumentContextEnabled
                ? compactChrome ? 'translate-x-[14px]' : 'translate-x-[18px]'
                : compactChrome ? 'translate-x-[3px]' : 'translate-x-[3px]'
            }`}
          />
        </button>
        {!compactChrome && (
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold w-6">
            {activeDocumentContextEnabled ? 'Ein' : 'Aus'}
          </span>
        )}
      </label>
    );
  };

  const shortDocName = useMemo(() => {
    const raw = documentDisplayName?.trim();
    if (!raw) return null;
    return raw.length > 28 ? `${raw.slice(0, 26)}…` : raw;
  }, [documentDisplayName]);

  const renderBackgroundJobChips = () => (
    pendingJobs.map((job) => (
      <span
        key={job.request_id}
        className="inline-flex items-center gap-0.5 rounded-full border border-violet-400/25 bg-violet-500/10 pl-1.5 pr-0.5 py-0"
      >
        <PortalBadge
          label={`Analysiere ${shortDocumentLabel(job.documentName)}…`}
          accent="violet"
          icon={<Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
          className="!border-0 !bg-transparent !px-0 !py-0 !text-[9px] !font-medium"
        />
        <button
          type="button"
          onClick={() => cancelJob(job.request_id)}
          className="rounded-full p-0.5 text-violet-200/80 hover:bg-violet-500/20 hover:text-violet-100"
          aria-label={`Analyse „${job.documentName}“ abbrechen`}
          title="Abbrechen"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      </span>
    ))
  );

  const renderCompletionNotices = () => (
    completionNotices.length > 0 ? (
      <div className="flex flex-col gap-1 px-2 py-1.5" aria-live="polite">
        {completionNotices.map((notice) => (
          <div
            key={notice.id}
            className={`inline-flex items-center gap-2 self-start rounded-full border px-2.5 py-1 text-[10px] font-medium ${
              notice.success
                ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100'
                : 'border-amber-400/25 bg-amber-500/10 text-amber-100'
            }`}
          >
            {notice.success ? (
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            ) : (
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            )}
            <span>{notice.summary}</span>
            <button
              type="button"
              onClick={() => dismissNotice(notice.id)}
              className="rounded-full p-0.5 opacity-70 hover:opacity-100"
              aria-label="Hinweis schließen"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </div>
        ))}
      </div>
    ) : null
  );

  const renderStatusBar = () => (
    <div className={`flex-shrink-0 ${PORTAL_CHAT.statusBar}`} aria-live="polite">
      {hasRunningJobs ? renderBackgroundJobChips() : null}
      {activeIntentLabel ? (
        <PortalBadge
          label={`Aktiv: ${activeIntentLabel}`}
          accent="blue"
          className="!px-1.5 !py-0 !text-[9px] !font-medium"
        />
      ) : (
        <span className="text-slate-600 italic">Kein Modus gewählt</span>
      )}
      <span className="text-slate-700" aria-hidden>
        ·
      </span>
      {docAvailable ? (
        <span className="truncate min-w-0 inline-flex items-center gap-0 min-w-0">
          <span className="text-violet-300/90 truncate">
            {synthesisDocumentLabels
              ? `ARKO nutzt: ${synthesisDocumentLabels}`
              : activeDocumentContextEnabled && shortDocName
                ? `ARKO arbeitet mit: ${shortDocName}`
                : 'Dokument-Kontext bereit'}
          </span>
          <span className="text-slate-700" aria-hidden>
            {' '}
            ·{' '}
          </span>
          <button
            type="button"
            onClick={() => toggleDocumentContext(!activeDocumentContextEnabled)}
            aria-pressed={activeDocumentContextEnabled}
            title={
              documentDisplayName?.trim()
                ? `Dokument-Kontext für „${documentDisplayName.trim()}“ umschalten`
                : 'Dokument-Kontext ein- oder ausschalten'
            }
            className={`inline-flex items-center rounded px-1 py-0 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-violet-400/40 ${
              activeDocumentContextEnabled
                ? 'text-violet-300 bg-violet-500/10 ring-1 ring-violet-400/25'
                : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.04]'
            }`}
          >
            Dokument-Kontext: {activeDocumentContextEnabled ? 'Ein' : 'Aus'}
          </button>
          {shortDocName ? (
            <span className="text-slate-500"> · {shortDocName}</span>
          ) : null}
        </span>
      ) : (
        <span className="text-slate-600">Kein Dokument geöffnet</span>
      )}
      {docAvailable ? (
        <>
          <span className="text-slate-700" aria-hidden>·</span>
          <button
            type="button"
            onClick={startBackgroundDocumentAnalysis}
            disabled={!canEnqueue || !backgroundAnalysisReady}
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-violet-400/20 bg-violet-500/[0.08] px-2 py-0.5 text-[9px] font-semibold text-violet-200 transition-colors hover:bg-violet-500/[0.16] disabled:cursor-not-allowed disabled:opacity-45"
            title={backgroundAnalysisTitle}
          >
            {hasRunningJobs ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="h-3 w-3" aria-hidden />
            )}
            {backgroundAnalysisLabel}
          </button>
        </>
      ) : null}
    </div>
  );

  const chatArea = (
    <div className="flex-1 min-h-0 flex flex-col">
      {renderCompletionNotices()}
      {activeThread ? (
        <InlineChat
          key={activeThread.id}
          webhookUrl={webhookUrl}
          title={title}
          subtitle={subtitle}
          initialMessage={initialBotMessage}
          accentColor={isCursorLayout || isCompactLayout ? 'cursor' : accentColor}
          mode={mode}
          extraBody={{
            ...(extraBody ?? {}),
            threadId: activeThread.id,
          }}
          documentContext={documentContext}
          documentContextEnabled={activeDocumentContextEnabled}
          documentDisplayName={documentDisplayName}
          activeDocumentId={activeDocumentId}
          openDocumentsMeta={openDocumentsMeta}
          selectedDocuments={selectedDocuments}
          synthesisDocumentLabels={synthesisDocumentLabels}
          documentExtractFingerprint={documentExtractFingerprint}
          documentAnalyzeDone={documentAnalyzeDone}
          onAnalyzeDocumentsReady={onAnalyzeDocumentsReady}
          timeoutMs={timeoutMs}
          showQuickActions={showQuickActions}
          showRechtsfrageQuickAction={showRechtsfrageQuickAction}
          usePortalChips={showQuickActions}
          chipAccent={isCursorLayout || isCompactLayout ? 'cursor' : accentColor === 'purple' ? 'violet' : 'blue'}
          hideHeader={hideChatHeader || isCursorLayout}
          compactChrome={isCompactLayout}
          shellClassName={chatShellClassName}
          chatVariant={isCursorLayout || isCompactLayout ? 'cursor' : 'default'}
          cursorCustomerName={cursorCustomerName}
          cursorKanzlei={cursorKanzlei}
          messages={activeThread.messages}
          onMessagesChange={activeMessagesSetter}
          enableContractIntent={enableContractIntent}
          onContractGenerate={onContractGenerate}
          onExportSchreibenPdf={onExportSchreibenPdf}
          onExportSchreibenDocx={onExportSchreibenDocx}
          onOpenPortalDocument={onOpenPortalDocument}
          onApplySynthesisToDocument={onApplySynthesisToDocument}
          synthesisHasOpenDocument={synthesisHasOpenDocument}
          onActiveIntentChange={handleActiveIntentChange}
          customerId={customerId}
          enableMemorySave={Boolean(memcore)}
          onMemorySaveRequest={memcore ? setMemThumb : undefined}
          memcoreRecall={memcore ?? null}
          pinnedMemContext={activePinnedMemContext}
          onPinFact={memcore ? pinFactToActiveThread : undefined}
          authToken={authToken}
          guidedFlowState={activeThread.guidedFlow ?? null}
          onGuidedFlowStateChange={handleGuidedFlowStateChange}
          paragraphIndexMap={paragraphIndexMap}
          cursorSelection={cursorSelection}
          onMarkDocumentAnalyzed={onMarkDocumentAnalyzed}
        />
      ) : null}
    </div>
  );

  const mainColumn = (
    <div className={`flex flex-col h-full min-h-0 flex-1 ${isCompactLayout ? 'gap-0' : 'gap-2'}`}>
      {isCompactLayout ? (
        <div className={`flex-shrink-0 ${PORTAL_CHAT.compactToolbar}`}>
          <div className="flex items-center gap-0.5 min-w-0 flex-1 overflow-x-auto custom-scrollbar">
            {renderThreadTabs()}
          </div>
          {renderNewChatButton()}
          {renderSessionEndButton()}
          {renderCaseFactsButton()}
          {renderArchiveToggle()}
          {docAvailable && (
            <>
              <span className={PORTAL_CHAT.compactToolbarDivider} aria-hidden />
              {renderDocumentContextToggle(true)}
            </>
          )}
        </div>
      ) : null}

      {(isCompactLayout || (showQuickActions && !isCursorLayout)) && renderStatusBar()}

      {!isCompactLayout && !isCursorLayout ? (
        <>
          <div className="flex items-center gap-1.5 flex-shrink-0 overflow-x-auto custom-scrollbar pb-1">
            {renderThreadTabs()}
            {renderNewChatButton()}
            {renderSessionEndButton()}
            {renderCaseFactsButton()}
            {renderArchiveToggle()}
          </div>

          {docAvailable && (
            <div className={`flex-shrink-0 flex items-center justify-between gap-3 px-2 py-1 ${PORTAL_CHAT.contextBar}`}>
              <span className="text-xs text-slate-300">Dokument-Kontext</span>
              {renderDocumentContextToggle()}
            </div>
          )}
          {!docAvailable && (
            <div className={`flex-shrink-0 flex items-center px-2 py-1 ${PORTAL_CHAT.contextBar}`}>
              <span className="text-[10px] text-slate-500 italic">Kein Dokument geöffnet</span>
            </div>
          )}
        </>
      ) : null}

      {isCursorLayout && showQuickActions ? renderStatusBar() : null}

      {warning && (
        <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center justify-between flex-shrink-0">
          <span>{warning}</span>
          <button
            onClick={() => setWarning(null)}
            className="text-amber-300/70 hover:text-amber-200 ml-3"
            aria-label="Hinweis schliessen"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {memcore && showCaseFacts && activeCaseFacts.length > 0 && (
        <div className={`flex-shrink-0 max-h-44 overflow-y-auto ${PORTAL_CHAT.archivePanel} p-2 space-y-1 custom-scrollbar`}>
          <div className="px-2 pb-1 text-[10px] text-slate-500">
            Fall-Eckdaten dieses Chats — ARKO bezieht sie automatisch in Entwürfe ein. Bleiben lokal in diesem Browser.
          </div>
          {activeCaseFacts.map(f => (
            <div key={f.id} className="flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800/60">
              <Brain className="w-3.5 h-3.5 text-blue-300/70 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 whitespace-pre-wrap break-words">{f.inhalt}</div>
                <div className="text-[10px] text-slate-500">
                  Gemerkt {new Date(f.ts).toLocaleString('de-AT')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeCaseFact(f.id)}
                className="p-1.5 rounded text-rose-300 hover:bg-rose-500/10 transition-colors flex-shrink-0"
                title="Aus dem Fall-Gedächtnis entfernen"
                aria-label="Fall-Eckdaten entfernen"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showArchive && archivedList.length > 0 && (
        <div className={`flex-shrink-0 max-h-44 overflow-y-auto ${PORTAL_CHAT.archivePanel} p-2 space-y-1 custom-scrollbar`}>
          {archivedList.map(a => (
            <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800/60">
              <Archive className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 truncate">{a.title}</div>
                <div className="text-[10px] text-slate-500">
                  {new Date(a.archivedAt).toLocaleString('de-AT')} · {a.messages.length} Nachrichten
                </div>
              </div>
              <button
                type="button"
                onClick={() => restoreFromArchive(a.id)}
                disabled={fullActive}
                className={`p-1.5 rounded transition-colors ${
                  fullActive
                    ? 'text-slate-600 cursor-not-allowed'
                    : 'text-violet-300 hover:bg-violet-500/10'
                }`}
                title={fullActive ? `Maximal ${PORTAL_MAX_OPEN_CHATS} aktive Chats` : 'Wiederherstellen'}
                aria-label="Aus Archiv wiederherstellen"
              >
                <ArchiveRestore className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => deleteArchived(a.id)}
                className="p-1.5 rounded text-rose-300 hover:bg-rose-500/10 transition-colors"
                title="Endgültig löschen"
                aria-label="Archivierten Chat endgültig löschen"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {chatArea}
    </div>
  );

  return (
    <>
      {isCursorLayout ? (
        <div className="arko-cursor-workspace flex flex-col flex-1 min-h-0 h-full w-full bg-[#07070a]">
          {renderCursorTopBar()}
          <div className="flex flex-col flex-1 min-h-0">{mainColumn}</div>
        </div>
      ) : (
        mainColumn
      )}

      {/* ── MEMCORE Modul B: Dialoge (Nudge, Session beenden, Daumen 2-Schritt) ── */}
      {memcore && memNudge && (() => {
        const t = state.threads[memNudge.threadId];
        if (!t) return null;
        return (
          <MemoryNudgeDialog
            threadTitle={t.title}
            onSave={() => {
              setMemSession({ threadId: memNudge.threadId, pendingAction: memNudge.action });
              setMemNudge(null);
            }}
            onDecline={() => {
              markMemNudgeDone(memNudge.threadId);
              const { threadId, action } = memNudge;
              setMemNudge(null);
              if (action === 'archive') archiveThreadNow(threadId);
              else deleteThreadNow(threadId);
            }}
            onCancel={() => setMemNudge(null)}
          />
        );
      })()}

      {memcore && memSession && (() => {
        const t = state.threads[memSession.threadId];
        if (!t) return null;
        return (
          <MemorySessionDialog
            ctx={{ kanzleiId: memcore.kanzleiId, userId: memcore.userId }}
            curatorUrl={memcore.curatorUrl}
            threadId={t.id}
            threadTitle={t.title}
            transcript={buildChatTranscript(t.messages)}
            onClose={() => setMemSession(null)}
            onFinished={() => {
              markMemNudgeDone(memSession.threadId);
              const { threadId, pendingAction } = memSession;
              setMemSession(null);
              if (pendingAction === 'delete') deleteThreadNow(threadId);
              else archiveThreadNow(threadId);
            }}
          />
        );
      })()}

      {memcore && memThumb && activeThread && (
        <MemoryThumbDialog
          ctx={{ kanzleiId: memcore.kanzleiId, userId: memcore.userId }}
          curatorUrl={memcore.curatorUrl}
          quelleRef={`portal:${customerId}:${activeThread.id}:${memThumb.messageId}`}
          nachricht={memThumb.text}
          onClose={() => setMemThumb(null)}
        />
      )}
    </>
  );
};

export default PortalArkoChatWorkspace;
